import moment from 'moment';
import _ from 'lodash';
import * as BECaller from '../../util/apiCaller';
import throttle from '../../util/throttle';
import ValuesService from '../../services/values.service';

import { setError } from '../../modules/App/ErrorsActions';
import { updateFieldData } from '../../modules/App/AppActions';
import {
  selectDashboard,
  success as updateDashboardsList,
  refetch as refetchDashboard
} from '../DashboardSelector/DashboardSelectorActions';
import { getColor, colorMap } from '../../util/colorMap';
import { adjustWhiteColor } from '../../util/colors';
import { getSelectionValues } from '../../util/filterUtils';
import { hasLabels } from '../../util/customFieldsUtils';

export const DEFAULT = 'FILTER_DEFAULT';
export const UPDATE_GLOBAL = 'FILTER_UPDATE_GLOBAL';
export const UPDATE = 'FILTER_UPDATE';
export const UPDATE_HASH = 'FILTER_UPDATE_HASH';
export const RESET_HASH = 'FILTERS_RESET_HASH';
export const CHART_TYPES = ['overview','process', 'cfd', 'ctsp', 'ctb', 'cth', 'cta', 'trc', 'th', 'fe', 'ddp', 'mc', 'mct'];
export const DEFAULT_CHART_TIMEUNITS = {
  'ctb': 'week',
  'cfd': 'month',
};

const ALL_FIELDS = [
  'controls',
  'filters',
  'lists',
  'labels',
  'members',
  'cf_',
  'aging_replay',
  'breakdown_by',
  'wip_breakdown',
  'wip_breakdown_by',
  'mc_sim_ctrls',
  'mct_sim_ctrls',
  'queue_states',
  'percent_range',
  'blocked_issues',
  'percentiles',
  'health_zones',
  'group_by',
  'trendlines',
  'enabled_color',
  'excl_zeros',
  'arr_dep_lines',
  'wip_tooltip',
  'ct_tooltip',
  'ct_precision',
  'date_format',
  'excl_weekends',
  'tasks_type',
  'done_status',
  'scale',
  'selected_date',
  'widgets',
  'widgetLayout'
];

const throttledSaveConfig = throttle(_saveConfig, 1500);
const throttledSaveGlobalConfig = throttle(_saveGlobalConfig, 1000);
const throttleHashUpdate = throttle(_updateHash, 1000);

export function setChart(chartName) {
  return (dispatch, getState) => {
    const { app } = getState().app;
    const { selected } = getState().dashboards;
    const filters = getState().filters;
    const { user } = getState().auth;    
    const insightsTrialExpired = user.config.aiInsightsStatus === 'expired';

    if (selected && selected.chartConfig) {
      if (chartName != 'wallmode') {
        setChartConfig(dispatch, selected, app, chartName, filters, 0, user.config.enableDoneFilters, insightsTrialExpired);
        dispatch(updateHash(chartName, selected.name));
      } else {
        let count = 0;
        CHART_TYPES.map(chart => {
          setChartConfig(dispatch, selected, app, chart, filters, count > 0, user.config.enableDoneFilters);
          count++;
        });
        dispatch(updateHash(null, selected.name));
      }
    }

    dispatch({
      type: UPDATE_GLOBAL,
      name: 'chart',
      data: chartName
    });
  };
}

export function initFilters(disable = {}, isProcessImprovement) {
  return (dispatch, getState) => {
    const { selected } = getState().dashboards;
    const { data, app, analysisData } = getState().app;
    const { user } = getState().auth;

    if (!selected || !selected.chartConfig) {
      return dispatch({
        type: DEFAULT
      });
    }
    const currentData = isProcessImprovement? analysisData: data;

    currentData.app = app;
    const service = new ValuesService(selected, currentData);

    const lists = service.GetDefaultFilterLists({
      excludeTodo: disable.todo,
      excludeDone: user.config.enableDoneFilters ? false : disable.done
    });
    dispatch(updateAvailableOptions('lists', lists.all));
    dispatch(updateSelectedOptions('lists', lists.selected));

    const labels = service.GetDefaultFilterLabels();
    dispatch(updateAvailableOptions('labels', labels.all));
    dispatch(updateSelectedOptions('labels', labels.selected));

    const members = service.GetDefaultFilterMembers();
    dispatch(updateAvailableOptions('members', members.all));
    dispatch(updateSelectedOptions('members', members.selected));

    const customFields = service.GetDefaultCustomFields();

    _.forEach(selected.customFilters, f => {
      const field = _.find(customFields, { name: f.name });
      const values =
        field &&
        _(f.values)
          .map(v => {
            const value = _.find(field.all, { label: v });
            if (value) {
              return value.value;
            }
          })
          .compact()
          .value();
      if (values.length) {
        f.fieldId = field.extId;
        f.values = values;
      }
    });

    dispatch(
      updateAvailableOptions(
        'customFields',
        customFields.map(cf => cf.extId)
      )
    );
    _.forEach(customFields, cf => {
      dispatch(updateAvailableOptions(cf.extId, cf.all, true));
      dispatch(updateSelectedOptions(cf.extId, _.sortBy(cf.selected), null, true));
    });

    const queueLists = service.GetDefaultFilterLists({
      excludeTodo: true,
      excludeDone: true
    });
    dispatch(updateAvailableOptions('queueLists', queueLists.all));
    dispatch(updateSelectedOptions('queueLists', []));

    dispatch(updateSelectedOptions('percentiles', ['50', '85', '95']));

    const todo = service.GetToDoItems();
    dispatch(updateCompletedItems(todo, 'mc'));

    const dones = service.GetDoneStatuses();
    dispatch(updateAvailableOptions('dones', dones.all));
    dispatch(updateSelectedOptions('dones', dones.selected));
  };
}

export function updateValues(field, isProcessImprovement) {
  return (dispatch, getState) => {
    const { selected } = getState().dashboards;
    const { data, app, analysisData } = getState().app;

    if (!field || !selected || !selected.chartConfig) {
      return false;
    }
    const currentData = isProcessImprovement? analysisData: data;
    currentData.app = app;
    const service = new ValuesService(selected, currentData);

    let updateColors = false;
    switch (field) {
      case 'labels': {
        const labels = service.GetDefaultFilterLabels();
        dispatch(updateAvailableOptions('labels', labels.all));
        updateColors = true;
        break;
      }
      case 'lists': {
        const lists = service.GetDefaultFilterLists();
        dispatch(updateAvailableOptions('lists', lists.all));

        const queueLists = service.GetDefaultFilterLists({
          excludeTodo: true,
          excludeDone: true
        });
        dispatch(updateAvailableOptions('queueLists', queueLists.all));
        break;
      }
      case 'members': {
        const members = service.GetDefaultFilterMembers();
        dispatch(updateAvailableOptions('members', members.all));
        break;
      }
      case 'customFields': {
        const customFields = service.GetDefaultCustomFields();
        dispatch(
          updateAvailableOptions(
            'customFields',
            customFields.map(cf => cf.extId)
          )
        );
        _.forEach(customFields, cf => {
          dispatch(updateAvailableOptions(cf.extId, cf.all, true));
        });
        updateColors = true;
        break;
      }
    }

    if (updateColors) {
      dispatch(updateFieldColors(!!selected.globalConfig ? selected.globalConfig.enabledColors : 'labels'));
    }
  };
}

function getFilterName(prefix, name, ignoreCase) {
  return prefix + (!ignoreCase ? name.replace(/^\w/, c => c.toUpperCase()) : name);
}

function baseOptionsUpdate(prefix, name, vals, ignoreCase) {
  return {
    type: UPDATE_GLOBAL,
    name: getFilterName(prefix, name, ignoreCase),
    data: vals
  };
}

function baseUpdate(name, val, chart) {
  return {
    type: UPDATE,
    chart: chart ? [chart] : null,
    name,
    data: val
  };
}

export function resetFiltersHash() {
  return {
    type: RESET_HASH
  };
}

export function updateHash(chart, name) {
  return {
    type: UPDATE_HASH,
    dashboard: name,
    chart: chart ? [chart] : null
  };
}

function updateAvailableOptions(name, vals, ignoreCase) {
  return baseOptionsUpdate('all', name, vals, ignoreCase);
}

export function updateEnabledColors(field, chart) {
  return {
    type: !!chart ? UPDATE : UPDATE_GLOBAL,
    name: 'enabledColors',
    chart: chart ? [chart] : null,
    data: field
  };
}

export function updateBreakdownField(field, chart) {
  return {
    type: UPDATE,
    name: 'breakdownBy',
    chart: chart ? [chart] : null,
    data: field
  };
}

export function updateDates(dates, filtersUpdated) {
  return {
    type: UPDATE_GLOBAL,
    name: 'dates',
    data: dates,
    filtersUpdated
  };
}

export function updateDateFormat(format) {
  return {
    type: UPDATE_GLOBAL,
    name: 'dateFormat',
    data: format
  };
}

export function updateTimePrecision(format) {
  return {
    type: UPDATE_GLOBAL,
    name: 'timePrecision',
    data: format
  };
}

export function updateExcludeWeekends(value) {
  return {
    type: UPDATE_GLOBAL,
    name: 'excludeWeekends',
    data: value
  };
}

export function updateActivePanel(value) {
  return {
    type: UPDATE_GLOBAL,
    name: 'activePanel',
    data: value
  };
}

export function updateWorkHours(isEnd, value) {
  return {
    type: UPDATE_GLOBAL,
    name: isEnd ? 'workEnd' : 'workStart',
    data: value
  };
}

export function updateHolidays(value) {
  return {
    type: UPDATE_GLOBAL,
    name: 'holidays',
    data: value
  };
}

export function updateSelectedOptions(name, selected, chart, ignoreCase) {
  return {
    type: UPDATE,
    chart: chart ? [chart] : null,
    name: 'selected' + (!ignoreCase ? name.replace(/^\w/, c => c.toUpperCase()) : name),
    data: selected
  };
}

export function updateEnableZero(enabled, chart) {
  return baseUpdate('excludeZeroDays', enabled, chart);
}

export function updateTrendLine(enabled, chart) {
  return baseUpdate('trendLine', enabled, chart);
}

export function updateArrivalDepartureLines(enabled, chart) {
  return baseUpdate('arrivalDepartureLines', enabled, chart);
}

export function updateShowNavigator(enabled, chart) {
  return baseUpdate('showNavigator', enabled, chart);
}

export function updateChartType(type, chart) {
  return baseUpdate('chartType', type, chart);
}

export function updateBlockedTime(type, chart){
  return baseUpdate('blockedTime', type, chart);
}

export function updateShowColumns(enabled, chart) {
  return baseUpdate('showColumns', enabled, chart);
}

export function updateLineChart(enabled, chart) {
  return baseUpdate('lineChart', enabled, chart);
}

export function updateAvgChart(enabled, chart) {
  return baseUpdate('avgChart', enabled, chart);
}

export function updateBlockersChart(enabled, chart) {
  return baseUpdate('blockersChart', enabled, chart);
}

export function updateBlockers(enabled, chart) {
  return baseUpdate('blockers', enabled, chart);
}

export function updateMetrics(enabled, chart) {
  return baseUpdate('metrics', enabled, chart);
}

export function updateBreakdown(enabled, chart) {
  return baseUpdate('breakdown', enabled, chart);
}

export function updateBreakdownType(type, chart) {
  return baseUpdate('breakdownType', type, chart);
}

export function updateWipBreakdownType(type, chart) {
  return baseUpdate('wipBreakdownType', type, chart);
}

export function updateWipBreakdownBy(field, chart) {
  return baseUpdate('wipBreakdownBy', field, chart);
}

export function updateWidgetMode(type, chart) {
  return baseUpdate('widgetMode', type, chart);
}

export function updateTimeUnit(unit, chart) {
  return baseUpdate('timeUnit', unit, chart);
}

export function updateDueDateTasks(type) {
  return baseUpdate('dueDateTasks', type, 'ddp');
}

export function updateWipStagesTooltip(val, chart) {
  return baseUpdate('wipStagesTooltip', val, chart);
}

export function updateApproxAvgStagesTooltip(val, chart) {
  return baseUpdate('approxAvgStagesTooltip', val, chart);
}

export function updateTrials(val, chart) {
  return baseUpdate('trials', val, chart);
}

export function updateCompletedItems(val, chart) {
  return baseUpdate('completedItems', val, chart);
}

export function updateTRCScale(val, chart) {
  return baseUpdate('scale', val, chart);
}

export function updateStartDate(val, chart, name = 'date') {
  return baseUpdate(name, val, chart);
}

export function updateEndDate(val, chart) {
  return baseUpdate('endDate', val, chart);
}

export function updateSelectedDate(val, chart) {
  return baseUpdate('selectedDate', val, chart);
}

export function updateEfficiencyRange(range, chart) {
  return baseUpdate('effRange', range, chart);
}

export function updateExpandedFilters(expanded, chart) {
  return baseUpdate('expandedFilters', expanded, chart);
}

export function updateFieldColors(fieldId, chartName) {
  return (dispatch, getState) => {
    if (fieldId === 'disabled' || !fieldId) {
      return false;
    }

    const filters = getState().filters;
    const fields = filters[getFilterName('all', fieldId, fieldId !== 'labels')];
    if (fields) {
      _.forEach(fields, (f, i) => {
        if (!f.color) {
          f.color = getColor();
        } else {
          f.color = colorMap(f.color);
        }
        f.color = adjustWhiteColor(f.color);
      });
    }
    dispatch(updateAvailableOptions(fieldId, fields, fieldId !== 'labels'));

    const { app, data, isLoading, wallmode } = getState().app;
    if (isLoading && !wallmode) {
      return false;
    }

    const fieldsToUpdate = [];
    const field = _.find(data.customFields, { extId: fieldId });

    if (!field) {
      dispatch(updateEnabledColors('labels', chartName));
      if (app === 'trello') {
        _.forEach(data.labels, o => {
          if (!o.color) {
              o.color = getColor();
          }
          fieldsToUpdate.push(o);          
        });
      } else if (app === 'azure') {
         _.forEach(data.labels, o => {
          const f = _.find(fields, { value: o.extId });
          if (f) {
            o.color = f.color;
            fieldsToUpdate.push(o);
          }
        });
      } else {
        return false;
      }
    } else {
      _.forEach(field.options, o => {
        const f = _.find(fields, { value: o._id });
        if (f) {
          o.color = f.color;
          fieldsToUpdate.push(o);
        }
      });
    }

    dispatch(updateFieldData(fieldId, fieldsToUpdate));
    dispatch(_saveFieldColors(fieldId, fieldsToUpdate));
  };
}

export function saveOptionColor(fieldId, optionId, color) {
  return (dispatch, getState) => {
    const { selected } = getState().dashboards;
    BECaller.api(`dashboards/fields/${selected.boardId}/${fieldId}`, 'post', { optionId, color })
      .then(() => {})
      .catch(err => {
        dispatch(handleError(err));
      });
  };
}

function _saveFieldColors(fieldId, options) {
  return dispatch => {
    BECaller.api(`dashboards/fields`, 'post', { fieldId, options })
      .then(() => {})
      .catch(err => {
        dispatch(handleError(err));
      });
  };
}

function _saveConfig(dispatch, getState) {
  const { selected, list } = getState().dashboards;
  const filters = getState().filters;
  if (filters.chart === 'wallmode') {
    return false;
  }

  const config = filters[filters.chart];

  const selectedCustomFields = _(filters.allCustomFields)
    .map(cf => {
      if (!!config[`selected${cf}`]) {
        return {
          fieldId: cf,
          values: getSelectionValues(config[`selected${cf}`], filters[`all${cf}`])
        };
      }
      return null;
    })
    .filter(cf => !!cf)
    .value();

  BECaller.api(`dashboards/${selected._id}/config`, 'put', {
    type: filters.chart,
    lists: getSelectionValues(config.selectedLists, filters.allLists),
    labels: getSelectionValues(config.selectedLabels, filters.allLabels),
    members: getSelectionValues(config.selectedMembers, filters.allMembers),
    dones: config.selectedDones.map(i => i.value._id),
    customFields: selectedCustomFields,
    queueLists: getSelectionValues(config.selectedQueueLists, filters.allLists),
    percentiles: config.selectedPercentiles,
    healthZones: config.selectedHealthZones,
    excludeZeroDays: config.excludeZeroDays,
    arrivalDepartureLines: config.arrivalDepartureLines,
    showNavigator: config.showNavigator,
    avgChart: config.avgChart,
    lineChart: config.lineChart,
    blockers: config.blockers,
    blockersChart: config.blockersChart,
    metrics: config.metrics,
    breakdown: config.breakdown,
    breakdownType: config.breakdownType,
    widgetMode: config.widgetMode,
    enabledColors: config.enabledColors,
    breakdownBy: config.breakdownBy,
    wipBreakdownType: config.wipBreakdownType,
    wipBreakdownBy: config.wipBreakdownBy,
    timeUnit: config.timeUnit,
    dueDateTasks: config.dueDateTasks,
    wipStagesTooltip: config.wipStagesTooltip,
    approxAvgStagesTooltip: config.approxAvgStagesTooltip,
    trendLine: config.trendLine,
    blockedTime: config.blockedTime,
    showColumns: config.showColumns,
    trials: config.trials,
    completedItems: config.completedItems,
    date: config.date,
    scale: config.scale,
    startDate: config.startDate,
    selectedDate: config.selectedDate,
    endDate: config.endDate,
    chartType: config.chartType,
    flowEfficiencyRange: config.effRange,
    expandedFilters: config.expandedFilters,
    widgets: config.selectedWidgets,
    widgetLayout: config.selectedWidgetLayout,
    ALL_FIELDS
  })
    .then(dashboard => {
      dispatch(updateList(list, dashboard));
      dispatch(selectDashboard({ ...selected, ...dashboard }));
    })
    .catch(err => {
      dispatch(handleError(err));
    });
}

export function saveConfig() {
  return (dispatch, getState) => {
    throttledSaveConfig(dispatch, getState);
    throttleHashUpdate(dispatch, getState);
  };
}

function _updateHash(dispatch, getState) {
  const filters = getState().filters;
  const { selected } = getState().dashboards;

  dispatch(updateHash(filters.chart, selected.name));
}

function _saveGlobalConfig(dispatch, getState) {
  const { selected, list } = getState().dashboards;
  const filters = getState().filters;

  BECaller.api(`dashboards/${selected._id}/config`, 'put', {
    global: {
      from: filters.dates.startDate,
      to: filters.dates.endDate,
      daysFromStart: filters.dates.daysFromStart,
      customDate: filters.dates.customDate,
      fixedStartDate: filters.dates.fixedStartDate,
      timePrecision: filters.timePrecision,
      excludeWeekends: filters.excludeWeekends,
      activePanel: filters.activePanel,
      workStart: filters.workStart,
      workEnd: filters.workEnd,
      holidays: filters.holidays,
      dateFormat: filters.dateFormat,
      enabledColors: filters.enabledColors
    }
  })
    .then(dashboard => {
      dispatch(updateList(list, dashboard));
      dispatch(selectDashboard({ ...selected, ...dashboard }));

      if (selected.dataFrom && moment(selected.dataFrom).isAfter(filters.dates.startDate)) {
        dispatch(refetchDashboard(selected._id, filters.dates.startDate));
      }
    })
    .catch(err => {
      dispatch(handleError(err));
    });
}

export function saveGlobalConfig(immediate, hash) {
  return (dispatch, getState) => {
    if (immediate) {
      _saveGlobalConfig(dispatch, getState);
    } else {
      throttledSaveGlobalConfig(dispatch, getState);
    }

    hash &&
      throttle((dispatch, getState) => {
        const { selected } = getState().dashboards;
        dispatch(updateHash(null, selected.name));
      }, 500)(dispatch, getState);
  };
}

export function saveFilterSize(size) {
  return dispatch => {
    BECaller.api(`account/config`, 'post', {
      key: 'filterSize',
      value: size
    }).then(
      () => {},
      err => {
        dispatch(handleError(err));
      }
    );
  };
}

function updateList(dashboards, updated) {
  let index = _.findIndex(dashboards, d => {
    return d._id == updated._id;
  });

  if (index > -1) {
    dashboards[index] = { ...dashboards[index], ...updated };
  }

  return updateDashboardsList(dashboards);
}

export function handleError(err, opt) {
  return setError(err, opt);
}

function setChartConfig(dispatch, dashboard, app, chartName, filters, globalUpdated, enableDoneFilters, insightsTrialExpired) {
  const config = dashboard.chartConfig;
  const globalConfig = dashboard.globalConfig;
  const fieldColors = hasLabels(app, dashboard.selectedFields) ? 'labels' : 'disabled';
  const customFilters = dashboard.customFilters;

  if (!globalUpdated) {
    setDates(globalConfig, dispatch, filters.dates);
    setTimePrecision(globalConfig, dispatch);
    setDateFormat(globalConfig, dispatch);
    setExcludeWeekends(globalConfig, dispatch);
    setActivePanel(globalConfig, dispatch, insightsTrialExpired);
    setWorkHours(globalConfig, dispatch);
  }

  if (dashboard.isCsv) {
    dispatch(updateTimePrecision('d[d]'));
    if (globalConfig && globalConfig.enabledColors) {
      setFieldColors(globalConfig, dispatch, null, fieldColors);
    } else {
      const field = filters.allCustomFields[0] || 'disabled';
      dispatch(updateEnabledColors(field));
      dispatch(updateFieldColors(field));
    }
  } else if (!globalUpdated) {
    setFieldColors(globalConfig, dispatch, null, fieldColors);
  }

  if (chartName !== 'overview' && chartName !== 'process' && chartName !== 'fe') {
    setQueueLists(config.fe, dispatch, filters.allLists, 'fe');
    setFlowEfficiencyRange(config.fe, dispatch, 'fe');
    setBlockedTime(config.fe, dispatch, 'fe');
    setBlockers(config.fe, dispatch, 'fe');
  }

  switch (chartName) {
    case 'overview': {
      setCommonFiltersConfig(config.overview, dispatch, filters, chartName, customFilters);
      setQueueLists(config.fe, dispatch, filters.allLists, chartName);
      setFlowEfficiencyRange(config.fe, dispatch, chartName);
      setBlockedTime(config.fe, dispatch, chartName);
      setBlockers(config.fe, dispatch, chartName);
      setWidgets(config.overview, dispatch, chartName);
      break;
    }
    case 'process': {
      setCommonFiltersConfig(config.process, dispatch, filters, chartName, customFilters);
      setQueueLists(config.fe, dispatch, filters.allLists, chartName);
      setFlowEfficiencyRange(config.fe, dispatch, chartName);
      setBlockedTime(config.fe, dispatch, chartName);
      setBlockers(config.fe, dispatch, chartName);
      setWidgets(config.process, dispatch, chartName);
      break;
    }   
    case 'cfd': {
      setCommonFiltersConfig(config.cfd, dispatch, filters, chartName, customFilters);
      setMetrics(config.cfd, dispatch, chartName);
      setArrivalDepartureLines(config.cfd, dispatch, chartName);
      config.cfd && dispatch(updateWipStagesTooltip(config.cfd.wipStagesTooltip, chartName));
      config.cfd && dispatch(updateApproxAvgStagesTooltip(config.cfd.approxAvgStagesTooltip, chartName));
      setTimeUnit(config.cfd, dispatch, chartName);
      setWidgetMode(config.cfd, dispatch, chartName);
      setFieldColors(config.cfd, dispatch, chartName, fieldColors);
      setAvgChart(config.cfd, dispatch, chartName);
      break;
    }
    case 'ctsp': {
      setPercentiles(config.ctsp, dispatch, chartName);
      setCommonFiltersConfig(config.ctsp, dispatch, filters, chartName, customFilters);
      setTimeUnit(config.ctsp, dispatch, chartName);
      setMetrics(config.ctsp, dispatch, chartName);
      setBreakdown(config.ctsp, dispatch, chartName);
      setBreakdownType(config.ctsp, dispatch, chartName);
      setTrendLine(config.ctsp, dispatch, chartName);
      setBlockedTime(config.ctsp, dispatch, chartName);
      setBlockers(config.ctsp, dispatch, chartName);
      setLineChart(config.ctsp, dispatch, chartName);
      setSelectedDate(config.ctsp, dispatch, chartName);
      break;
    }
    case 'ctb': {
      setCommonFiltersConfig(config.ctb, dispatch, filters, chartName, customFilters);
      setTimeUnit(config.ctb, dispatch, chartName);
      setBreakdown(config.ctb, dispatch, chartName);
      setBreakdownType(config.ctb, dispatch, chartName);
      setBlockers(config.ctb, dispatch, chartName);
      setBlockedTime(config.ctb, dispatch, chartName);
      setBlockersChart(config.ctb, dispatch, chartName);
      setBreakdownBy(config.ctb, dispatch, chartName);
      break;
    }
    case 'cth': {
      setPercentiles(config.cth, dispatch, chartName);
      setCommonFiltersConfig(config.cth, dispatch, filters, chartName, customFilters);
      setShowNavigator(config.cth, dispatch, chartName);
      setAvgChart(config.cth, dispatch, chartName);
      setTimeUnit(config.cth, dispatch, chartName);
      setBlockedTime(config.cth, dispatch, chartName);
      setBlockers(config.cth, dispatch, chartName);
      break;
    }
    case 'cta': {
      setPercentiles(config.cta, dispatch, chartName);
      setHealthZones(config.cta, dispatch, chartName);
      setCommonFiltersConfig(config.cta, dispatch, filters, chartName, customFilters);
      setTimeUnit(config.cta, dispatch, chartName);
      setBlockedTime(config.cta, dispatch, chartName);
      setAvgChart(config.cta, dispatch, chartName);
      setBlockers(config.cta, dispatch, chartName);
      setBlockersChart(config.cta, dispatch, chartName);
      setLineChart(config.cta, dispatch, chartName);
      setBreakdownBy(config.cta, dispatch, chartName);
      setShowColumns(config.cta, dispatch, chartName);
      setWipBreakdownType(config.cta, dispatch, chartName);
      setWipBreakdownBy(config.cta, dispatch, chartName);
      break;
    }
    case 'trc': {
      setCommonFiltersConfig(config.trc, dispatch, filters, chartName, customFilters);
      setTimeUnit(config.trc, dispatch, chartName);
      setLineChart(config.trc, dispatch, chartName);
      setBreakdown(config.trc, dispatch, chartName);
      setBreakdownType(config.trc, dispatch, chartName);
      setTrendLine(config.trc, dispatch, chartName);
      setFieldColors(config.trc, dispatch, chartName, fieldColors);
      setSelectedDate(config.trc, dispatch, chartName);
      setBlockedTime(config.trc, dispatch, chartName);
      setBlockers(config.trc, dispatch, chartName);
      break;
    }
    case 'th': {
      setPercentiles(config.th, dispatch, chartName);
      setCommonFiltersConfig(config.th, dispatch, filters, chartName, customFilters);
      setEnableZero(config.th, dispatch, chartName);
      setShowNavigator(config.th, dispatch, chartName);
      setAvgChart(config.th, dispatch, chartName);
      setTimeUnit(config.th, dispatch, chartName);
      break;
    }
    case 'fe': {
      const lists = _.filter(filters.allLists, l => {
        return dashboard.todo.indexOf(l.value._id) === -1 && 
               (!enableDoneFilters ? dashboard.done.indexOf(l.value._id) === -1 : true);
      });
      setCommonFiltersConfig(config.fe, dispatch, filters, chartName, customFilters);
      setLists(config.fe, dispatch, lists, chartName);
      setQueueLists(config.fe, dispatch, lists, chartName);
      setShowNavigator(config.fe, dispatch, chartName);
      setAvgChart(config.fe, dispatch, chartName);
      setFlowEfficiencyRange(config.fe, dispatch, chartName);
      setChartType(config.fe, dispatch, chartName);
      setBlockedTime(config.fe, dispatch, chartName);
      setBlockers(config.fe, dispatch, chartName);
      break;
    }
    case 'ddp': {
      setBlockedTime(config.ddp, dispatch, chartName);
      setBlockers(config.ddp, dispatch, chartName);
      setCommonFiltersConfig(config.ddp, dispatch, filters, chartName, customFilters);
      setTimeUnit(config.ddp, dispatch, chartName);
      setLineChart(config.ddp, dispatch, chartName);
      setDueDateTasks(config.ddp, dispatch);
      setDoneStatuses(config.ddp, dispatch, filters.allDones, chartName);
      break;
    }
    case 'mc': {
      setCommonFiltersConfig(config.mc, dispatch, filters, chartName, customFilters);
      setPercentiles(config.mc, dispatch, chartName);
      setShowNavigator(config.mc, dispatch, chartName, true);
      setMonteCarloOptions(config.mc, dispatch, chartName);
      break;
    }
    case 'mct': {
      setCommonFiltersConfig(config.mct, dispatch, filters, chartName, customFilters);
      setPercentiles(config.mct, dispatch, chartName);
      setShowNavigator(config.mct, dispatch, chartName, true);
      setMonteCarloOptions(config.mct, dispatch, chartName);
      break;
    }
  }
}

function setDates(model, dispatch, currentDates) {
  if (!model || (!model.customDate && typeof model.dateDiff !== 'number')) {
    model.dateDiff = moment().diff(moment().subtract(3, 'months'), 'day');
  }

  let from, to;
  if (model.customDate) {
    from = moment(model.from).startOf('day');
    to = moment(model.to).endOf('day');
  } else {
    to = moment().endOf('day');
    if (model.fixedStartDate) {
      from = moment(model.from).startOf('day');
    } else {
      from = moment()
        .subtract(model.dateDiff, 'day')
        .startOf('day');
    }
  }

  dispatch(
    updateDates({
      ...currentDates,
      customDate: model.customDate,
      fixedStartDate: model.fixedStartDate,
      startDate: from,
      endDate: to,
      singleDate: to,
      daysFromStart: to.diff(from, 'days')
    })
  );
}

function setCommonFiltersConfig(model, dispatch, filters, chartName, customFilters) {
  setLists(model, dispatch, filters.allLists, chartName);
  setLabels(model, dispatch, filters.allLabels, chartName);
  setMembers(model, dispatch, filters.allMembers, chartName);
  setCustomFields(model, dispatch, filters, chartName, customFilters);
  setExpandedFilters(model, dispatch, chartName);
}

function setLists(model, dispatch, allLists, chartName) {
  if (model && model.lists) {
    dispatch(
      updateSelectedOptions(
        'lists',
        mapIdToLists(
          getSelectionValues(model.lists, allLists, a => {
            return a.value._id;
          }),
          allLists
        ),
        chartName
      )
    );
  }
}

function setDoneStatuses(model, dispatch, allDones, chartName) {
  if (model && model.dones && model.dones.length) {
    dispatch(
      updateSelectedOptions(
        'dones',
        allDones.filter(i => model.dones[0] === i.value._id),
        chartName
      )
    );
  }
}

function setQueueLists(model, dispatch, allLists, chartName) {
  if (model && model.queueLists) {
    dispatch(
      updateSelectedOptions(
        'queueLists',
        mapIdToLists(
          getSelectionValues(model.queueLists, allLists, a => {
            return a.value._id;
          }),
          allLists
        ),
        chartName
      )
    );
  }
}

function setLabels(model, dispatch, allLabels, chartName) {
  if (model && model.labels && model.labels.length) {
    dispatch(updateSelectedOptions('labels', getSelectionValues(model.labels, allLabels), chartName));
  }
}

function setMembers(model, dispatch, allMembers, chartName) {
  if (model && model.members && model.members.length) {
    if (allMembers && allMembers.length) {
      dispatch(updateSelectedOptions('members', getSelectionValues(model.members, allMembers), chartName));
    }
  }
}

function setCustomFields(model, dispatch, filters, chartName, customFilters) {
  if (model && model.customFields && model.customFields.length) {
    _.forEach(filters.allCustomFields, cf => {
      const allValues = filters[`all${cf}`];
      if (allValues) {
        const field = _.find(model.customFields, { fieldId: cf });
        if (field) {
          let selectedValues = getSelectionValues(field.values, allValues);
          const customFilter = _.find(customFilters, { fieldId: cf });
          if (customFilter && customFilter.values && customFilter.values.length) {
            selectedValues = customFilter.values;
          }
          dispatch(updateSelectedOptions(cf, _.sortBy(selectedValues), chartName, true));
        }
      }
    });
  }
}

export function mapIdToLists(ids, lists) {
  return _(ids)
    .map(id => {
      let list = _.find(lists, l => l.value._id == id);
      if (list) {
        return list.value;
      }
    })
    .filter(l => {
      return !!l;
    })
    .value();
}

function setPercentiles(model, dispatch, chartName) {
  if (model && model.percentiles && model.percentiles.length) {
    dispatch(updateSelectedOptions('percentiles', model.percentiles, chartName));
  }
}

function setHealthZones(model, dispatch, chartName) {
  if (model && model.healthZones) {
    dispatch(updateSelectedOptions('healthZones', model.healthZones, chartName));
  } else {
    dispatch(updateSelectedOptions('healthZones', ['30', '50', '70', '85', '95'], chartName));
  }
}

function setFlowEfficiencyRange(model, dispatch, chartName) {
  if (model && model.effRange) {
    dispatch(updateEfficiencyRange(model.effRange));
  } else {
    dispatch(updateEfficiencyRange([0, 100]));
  }
}

function setChartType(model, dispatch, chartName) {
  if (model && model.chartType) {
    dispatch(updateChartType(model.chartType));
  } else {
    dispatch(updateChartType('column'));
  }
}

function setEnableZero(model, dispatch, chartName) {
  if (model) {
    dispatch(updateEnableZero(model.excludeZeroDays, chartName));
  } else {
    dispatch(updateEnableZero(true, chartName));
  }
}

function setArrivalDepartureLines(model, dispatch, chartName) {
  if (model) {
    dispatch(updateArrivalDepartureLines(model.arrivalDepartureLines, chartName));
  } else {
    dispatch(updateArrivalDepartureLines(true, chartName));
  }
}

function setTimeUnit(model, dispatch, chartName) {
  if (model && model.timeUnit) {
    if (['ctsp', 'cta', 'cth'].indexOf(chartName) > -1 && ['day', 'week', 'month'].indexOf(model.timeUnit) === -1) {
      dispatch(updateTimeUnit('day', chartName));
    } else {
      dispatch(updateTimeUnit(model.timeUnit, chartName));
    }
  } else {
    dispatch(updateTimeUnit(DEFAULT_CHART_TIMEUNITS[chartName] || 'day', chartName));
  }
}

function setDueDateTasks(model, dispatch) {
  if (model && model.dueDateTasks) {
    dispatch(updateDueDateTasks(model.dueDateTasks, 'ddp'));
  } else {
    dispatch(updateDueDateTasks('all', 'ddp'));
  }
}

function setTimePrecision(model, dispatch) {
  if (model && model.timePrecision) {
    dispatch(updateTimePrecision(model.timePrecision));
  } else {
    dispatch(updateTimePrecision('d[d] h[h] m[m]'));
  }
}

function setExcludeWeekends(model, dispatch) {
  if (model && !!model.excludeWeekends) {
    dispatch(updateExcludeWeekends(model.excludeWeekends));
  } else {
    dispatch(updateExcludeWeekends(0));
  }
}

function setActivePanel(model, dispatch, insightsTrialExpired) {
  if (insightsTrialExpired) {
    dispatch(updateActivePanel('filters'));
  } else if (model && !!model.activePanel) {
    dispatch(updateActivePanel(model.activePanel));
  } else {
    dispatch(updateActivePanel('insights'));
  }
}

function setWorkHours(model, dispatch) {
  if (model && model.workStart) {
    if (model.workStart.indexOf(':') > -1) {
      dispatch(updateWorkHours(false, model.workStart));
    } else {
      dispatch(updateWorkHours(false, `${model.workStart}:00`));
    }
  } else {
    dispatch(updateWorkHours(false, '09:00'));
  }

  if (model && model.workEnd) {
    if (model.workEnd.indexOf(':') > -1) {
      dispatch(updateWorkHours(true, model.workEnd));
    } else {
      dispatch(updateWorkHours(true, `${model.workEnd}:00`));
    }
  } else {
    dispatch(updateWorkHours(true, '17:00'));
  }

  if (model && model.holidays) {
    dispatch(updateHolidays(model.holidays));
  } else {
    dispatch(updateHolidays([]));
  }
}

function setDateFormat(model, dispatch) {
  if (model && model.dateFormat) {
    dispatch(updateDateFormat(model.dateFormat));
  } else {
    dispatch(updateDateFormat('DD MMM YYYY'));
  }
}

function setFieldColors(model, dispatch, chartName, defaultVal = 'labels') {
  if (model && model.enabledColors) {
    dispatch(updateEnabledColors(model.enabledColors, chartName));
    dispatch(updateFieldColors(model.enabledColors, chartName));
  } else {
    dispatch(updateEnabledColors(defaultVal, chartName));
    dispatch(updateFieldColors(defaultVal, chartName));
  }
}

function setShowNavigator(model, dispatch, chartName, defaultValue = false) {
  if (model) {
    dispatch(updateShowNavigator(model.showNavigator, chartName));
  } else {
    dispatch(updateShowNavigator(defaultValue, chartName));
  }
}

function setLineChart(model, dispatch, chartName) {
  if (model) {
    dispatch(updateLineChart(model.lineChart, chartName));
  } else {
    dispatch(updateLineChart(true, chartName));
  }
}

function setAvgChart(model, dispatch, chartName) {
  if (model) {
    dispatch(updateAvgChart(model.avgChart, chartName));
  } else {
    dispatch(updateAvgChart(true, chartName));
  }
}

function setMetrics(model, dispatch, chartName) {
  if (model) {
    dispatch(updateMetrics(model.metrics, chartName));
  } else {
    dispatch(updateMetrics(true, chartName));
  }
}

function setBreakdown(model, dispatch, chartName) {
  if (model) {
    dispatch(updateBreakdown(model.breakdown, chartName));
  } else {
    dispatch(updateBreakdown(true, chartName));
  }
}

function setBreakdownType(model, dispatch, chartName) {
  if (model && model.breakdownType) {
    dispatch(updateBreakdownType(model.breakdownType, chartName));
  } else {
    dispatch(updateBreakdownType('pie', chartName));
  }
}

function setWidgetMode(model, dispatch, chartName) {
  if (model && model.widgetMode) {
    dispatch(updateWidgetMode(model.widgetMode, chartName));
  } else {
    dispatch(updateWidgetMode('line', chartName));
  }
}

function setTrendLine(model, dispatch, chartName) {
  if (model) {
    dispatch(updateTrendLine(model.trendLine, chartName));
  } else {
    dispatch(updateTrendLine(true, chartName));
  }
}

function setBlockedTime(model, dispatch, chartName) {
  if (model && model.blockedTime) {
    dispatch(updateBlockedTime(model.blockedTime, chartName));
  } else {
    dispatch(updateBlockedTime('all', chartName));
  }
}

function setBlockersChart(model, dispatch, chartName) {
  if (model && model.blockersChart !== undefined) {
    dispatch(updateBlockersChart(model.blockersChart, chartName));
  } else {
    dispatch(updateBlockersChart(true, chartName));
  }
}

function setBlockers(model, dispatch, chartName) {
  if (model && model.blockers !== undefined) {
    dispatch(updateBlockers(model.blockers, chartName));
  } else {
    dispatch(updateBlockers(true, chartName));
  }
}

function setShowColumns(model, dispatch, chartName) {
  if (model) {
    dispatch(updateShowColumns(model.showColumns, chartName));
  } else {
    dispatch(updateShowColumns(false, chartName));
  }
}

function setMonteCarloOptions(model, dispatch, chartName) {
  if (model) {
    if (model.trials) {
      dispatch(updateTrials(model.trials, chartName));
    } else {
      dispatch(updateTrials(10000, chartName));
    }

    if (model.scale) {
      dispatch(updateTRCScale(model.scale, chartName));
    } else {
      dispatch(updateTRCScale(1, chartName));
    }
    if (model.completedItems) {
      dispatch(updateCompletedItems(model.completedItems, chartName));
    }
    else{
      dispatch(updateCompletedItems(220, chartName));
    }

    if (model.startDate) {
      dispatch(updateStartDate(model.startDate, chartName, 'startDate'));
    }
    else {
      dispatch(updateStartDate(moment().add(1,'day'), chartName, 'startDate'));
    }
    
    if (model.date) {
      dispatch(updateStartDate(model.date, chartName, 'date'));
    } 
    else {
      dispatch(updateStartDate(moment(), chartName, 'date'));
    }

    if (model.endDate) {
      dispatch(updateEndDate(model.endDate, chartName));
    } 
    else {
      dispatch(updateEndDate(moment().add(31,'day'), chartName, 'date'));
    }
  }
}

function setSelectedDate(model, dispatch, chartName) {
  if (model) {
    if (model.selectedDate) {
      dispatch(updateSelectedDate(model.selectedDate, chartName));
    } else {
      dispatch(updateSelectedDate('end', chartName));
    }
  }
}

function setExpandedFilters(model, dispatch, chartName) {
  if (model && model.expandedFilters) {
    dispatch(updateExpandedFilters(model.expandedFilters, chartName));
  } else {
    dispatch(updateExpandedFilters(ALL_FIELDS, chartName));
  }
}

function setWidgets(model, dispatch, chartName) {
  if (model && model.widgets && model.widgets.length > 0) {
    dispatch(updateSelectedOptions('widgets', model.widgets, chartName));
  }
  if (model && model.widgetLayout && model.widgetLayout.length > 0) {
    dispatch(updateSelectedOptions('widgetLayout', model.widgetLayout, chartName));
  }
}

function setBreakdownBy(model, dispatch, chartName) {
  if (model && model.breakdownBy) {
    dispatch(updateBreakdownField(model.breakdownBy, chartName));
    dispatch(updateEnabledColors(model.breakdownBy, chartName));
  } else {
    dispatch(updateBreakdownField('lists', chartName));
    dispatch(updateEnabledColors('lists', chartName));
  }
}

function setWipBreakdownType(model, dispatch, chartName) {
  if (model && model.wipBreakdownType) {
    dispatch(updateWipBreakdownType(model.wipBreakdownType, chartName));
  } else {
    dispatch(updateWipBreakdownType('line', chartName));
  }
}

function setWipBreakdownBy(model, dispatch, chartName) {
  if (model && model.wipBreakdownBy) {
    dispatch(updateWipBreakdownBy(model.wipBreakdownBy, chartName));
  } else {
    dispatch(updateWipBreakdownBy('lists', chartName));
  }
}
